import React, { createContext, useContext, useEffect, useState } from "react"

import axios from "axios"

// Define types
export interface UserData {
  id: number
  firstName: string
  lastName: string
  email: string
  testGroupIndex: number
}

interface UserContextData {
  data: UserData

  populateUserData: (newData?: UserData) => void
  loading: boolean

  loginFormOpen: boolean
  setLoginFormOpen: (value: boolean) => void
}

const UserContext = createContext<UserContextData>({
  data: {
    id: NaN,
    firstName: "",
    lastName: "",
    email: "",
    testGroupIndex: 0,
  },

  populateUserData: () => {},
  loading: true,

  loginFormOpen: false,
  setLoginFormOpen: () => {},
})

// Component props template
interface UserProviderProps {
  children: React.ReactNode
}

// Define component
export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  // Initialize state
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [email, setEmail] = useState("")
  const [id, setId] = useState(NaN)
  const [testGroupIndex, setTestGroupIndex] = useState(0)
  const [loading, setLoading] = useState(true)

  const [loginFormOpen, setLoginFormOpen] = useState(false)

  // Check if the user has a temporary store token and that it's unexpired
  const checkStoreToken = async () => {
    let storeToken = localStorage.getItem("STORE_TOKEN")
    let storeTokenExpiration = localStorage.getItem("STORE_TOKEN_EXPIRATION")

    if (
      !storeToken ||
      !storeTokenExpiration ||
      Number(storeTokenExpiration) < new Date().getTime() / 1000
    ) {
      try {
        const res = await axios.get("/.netlify/functions/create_store_token")

        storeToken = res.data.token
        storeTokenExpiration = res.data.tokenExpiration
        localStorage.setItem("STORE_TOKEN", storeToken)
        localStorage.setItem("STORE_TOKEN_EXPIRATION", storeTokenExpiration)
      } catch (error) {
        console.error(error)
      }
    }
  }

  const populateUserData = async (newData?: UserData) => {
    setLoading(true)

    await checkStoreToken()

    if (localStorage.getItem("testGroupIndex")) {
      setTestGroupIndex(parseInt(localStorage.getItem("testGroupIndex")))
    } else {
      const newTestGroup = Math.random() < 0.5 ? 0 : 1
      setTestGroupIndex(newTestGroup)
      localStorage.setItem("testGroupIndex", newTestGroup.toString())
    }

    if (newData) {
      setFirstName(newData.firstName)
      setLastName(newData.lastName)
      setEmail(newData.email)
      setId(newData.id)
    } else {
      const storeToken = localStorage.getItem("STORE_TOKEN") || ""

      try {
        const res = await axios.post(
          `/graphql`,
          {
            query: `
              query CustomerAttributes {
                customer {
                  firstName
                  lastName
                  email
                  entityId
                  customerGroupId
                }
              }
            `,
          },
          {
            headers: {
              Authorization: `Bearer ${storeToken}`,
              "Content-Type": "application/json",
              Accept: "application/json",
            },
          },
        )

        const customerData = res.data.data.customer
        // if (!customerData) throw new Error("User is probably not logged in!")

        if (
          customerData &&
          customerData.firstName &&
          customerData.lastName &&
          customerData.email &&
          customerData.entityId
        ) {
          setFirstName(customerData.firstName)
          setLastName(customerData.lastName)
          setEmail(customerData.email)
          setId(customerData.entityId)
        }
      } catch (error) {
        console.error(error)
      }
    }

    setLoading(false)
  }

  useEffect(() => {
    populateUserData(
      process.env.NODE_ENV == "development"
        ? {
            firstName: "Bingus",
            lastName: "McBongus",
            email: "bingus@bongus.sus",
            id: 1,
            testGroupIndex: 0,
          }
        : undefined,
    )
  }, [])

  return (
    <UserContext.Provider
      value={{
        data: {
          firstName,
          lastName,
          email,
          id,
          testGroupIndex,
        },

        populateUserData,
        loading,

        loginFormOpen,
        setLoginFormOpen,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}

export function useUser() {
  return useContext(UserContext)
}
